import { useState, useMemo, useCallback } from 'react';
import { mergeEdits } from './playerPreferenceMerge';

/**
 * Replaces the legacy `PreferenceStateUtil` HOC for the PMM v4 player
 * editor path. Holds the partial bag of pending edits in `useState`
 * and exposes `merged` -- the working state the editor renders and
 * eventually saves -- as a memoized overlay on `originalProperties`.
 *
 * No reach into props or actions; the caller orchestrates save.
 *
 * @param {Object<string, any>} originalProperties  flat properties from PlayerStore (post-`normalize`), plus `name`
 * @returns {{
 *   edits: Object<string, any>,
 *   merged: Object<string, any>,
 *   storeEdits: (propertyKey: string, value: any) => void,
 *   undoEdits: () => void,
 * }}
 */
export function useEditState (originalProperties) {
  const [edits, setEdits] = useState({});

  const merged = useMemo(
    () => mergeEdits(originalProperties, edits),
    [originalProperties, edits]
  );

  const storeEdits = useCallback((propertyKey, value) => {
    setEdits(prev => ({ ...prev, [propertyKey]: value }));
  }, []);

  const undoEdits = useCallback(() => setEdits({}), []);

  return { edits, merged, storeEdits, undoEdits };
}
